import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SupportingDocumentValidationSchema } from "./common/eventApprovalValidationSchemas";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { deletePcsEventDocumentAsync, getPcsEventDocumentAsync, getPcsEventDocumentsAsync, savePcsEventDocumentAsync } from "../../app/slices/pcsEventSlice";
import { API_REQUEST_FULLFILLED, MESSAGE_FILE_UPLOADED_SCESSFULLY, MESSAGE_NEXT, MESSAGE_PROCESSING_DETAILS } from "../../app/utils/constant";
import { toast } from "react-toastify";
import AppFileInput from "../../app/components/AppFileInput";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { useEffect, useState } from "react";
import React from "react";
import { formatBytes, getLocalDateWithTimeInString } from "../../app/utils/util";
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { GetPcsEventDocumentParamObj } from "./common/getPcsEventDocumentParam";
import UnsavedChangesDialog from "../../app/components/UnsavedChangesDialog";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import PcsEventDialog from "./common/PcsEventDialog";
import EventDocumentsMediaCard from "./common/EventDocumentsMediaCard";
import SupportingDocumentsHeader from "./common/SupportingDocumentsHeader";

interface Props {
    isSpaEditabe: boolean;
    handleNext: () => void;
    handleBack: () => void;
}

export default function SupportingDocuments({ isSpaEditabe, handleNext, handleBack }: Props) {
    const { SupportingDocument } = EventsFormModel.documentFormField;

    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(SupportingDocumentValidationSchema)
    });
    const formObj = methods.watch();
    const { control, trigger } = methods;

    const { pcsEventDetails, pcsEventDocuments, getPcsEventDocumentsStatus, getPcsEventDocumentStatus,
        savePcsEventDocumentStatus, deletePcsEventDocumentStatus } = useAppSelector(state => state.pcsEvent);

    const [deleteDocumentConfirmation, setDeleteDocumentConfirmation] = useState(false);
    const [fileToDelete, setFileToDelete] = useState('');
    const [showDocumentError, setShowDocumentError] = useState(false);

    const populateFileFormData = (file: File) => {
        const formData = new FormData();
        if (pcsEventDetails?.eventId) {
            formData.append("EventId", pcsEventDetails?.eventId);
            formData.append("File", file);
        }
        return formData;
    }

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (pcsEventDetails?.eventId) {
            dispatch(getPcsEventDocumentsAsync(pcsEventDetails?.eventId));
        }
    }, [pcsEventDetails, dispatch]);

    const saveDetails = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) trackPortalEvent(portalScreenContext('SupportingDocuments'), 'ValidationBlocked', { step: 'documents' });

        if (isStepValid) {
            dispatch(savePcsEventDocumentAsync(populateFileFormData(formObj.SupportingDocument as unknown as File))).then((response: any) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    pcsEventDetails?.eventId && dispatch(getPcsEventDocumentsAsync(pcsEventDetails?.eventId))
                    toast.success(MESSAGE_FILE_UPLOADED_SCESSFULLY);
                };
            });
        }
    }

    const downloadFile = (fileName: string | null) => {
        if (pcsEventDetails?.eventId && pcsEventDetails !== null && fileName !== null) {
            const getPcsEventDocumentParamObj: GetPcsEventDocumentParamObj = {
                eventId: pcsEventDetails?.eventId,
                fileName: fileName
            };
            dispatch(getPcsEventDocumentAsync(getPcsEventDocumentParamObj)).then((response: any) => {
                const url = window.URL.createObjectURL(new Blob([response.payload], { type: 'application/octet-stream' }));
                const link = document.createElement('a');

                link.href = url;
                link.download = response.meta.arg.fileName;

                document.body.appendChild(link);

                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
            });
        }
    }

    const deleteDocument = (fileName: string) => {
        if (!pcsEventDetails?.eventId) return;

        const getPcsEventDocumentParamObj: GetPcsEventDocumentParamObj = {
            eventId: pcsEventDetails?.eventId,
            fileName: fileName
        };
        dispatch(deletePcsEventDocumentAsync(getPcsEventDocumentParamObj))
            .then((response) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    pcsEventDetails?.eventId && dispatch(getPcsEventDocumentsAsync(pcsEventDetails?.eventId));
                }
            });
    }

    const onNextClick = () => {
        if (pcsEventDocuments?.length && pcsEventDocuments?.length > 0) {
            handleNext();
        }
        else {
            trackPortalEvent(portalScreenContext('SupportingDocuments'), 'ValidationBlocked', { step: 'documents' });
            setShowDocumentError(true);
        }
    }

    usePortalScreen(portalScreenContext('SupportingDocuments'), undefined, !(getPcsEventDocumentsStatus === ApiRequestStatus.Pending || getPcsEventDocumentStatus === ApiRequestStatus.Pending ||
        savePcsEventDocumentStatus === ApiRequestStatus.Pending || deletePcsEventDocumentStatus === ApiRequestStatus.Pending));

    if (getPcsEventDocumentsStatus === ApiRequestStatus.Pending || getPcsEventDocumentStatus === ApiRequestStatus.Pending ||
        savePcsEventDocumentStatus === ApiRequestStatus.Pending || deletePcsEventDocumentStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ ml: '2%', mr: '2%' }}>

                <Grid size={{ xs: 12 }}></Grid>

                {isSpaEditabe === false && <>
                    <SupportingDocumentsHeader />

                    <Grid size={{ xs: 12 }}></Grid>

                    <Grid size={{ xs: 4, sm: 3 }}>
                        <b>Supporting Document*</b>
                    </Grid>

                    <Grid size={{ xs: 8, sm: 9 }}>
                        <AppFileInput control={control} label={""} name={SupportingDocument.name} disabled={isSpaEditabe} />
                    </Grid>


                    <Grid size={{ xs: 4, sm: 3 }}></Grid>

                    <Grid size={{ xs: 8, sm: 9 }}>
                        <Button variant="contained" component="span" onClick={saveDetails}>
                            Add This Document
                        </Button>
                    </Grid>

                    <Grid size={{ xs: 12 }}></Grid>
                </>}
            </Grid>

            <Grid container rowSpacing={4} sx={{ ml: '2%' }} display={{ xs: 'none', sm: 'none', md: 'flex' }}>
                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 2.5 }} className="wordWrap">
                    <b>File Name</b>
                </Grid>

                <Grid size={{ xs: 2.5 }} className="wordWrap">
                    <b>File size</b>
                </Grid>

                <Grid size={{ xs: 2.5 }} className="wordWrap">
                    <b>Last modified</b>
                </Grid>

                <Grid size={{ xs: 4.5 }} className="wordWrap">

                </Grid>

                {pcsEventDocuments?.map((item) => (
                    <React.Fragment key={item.name}>
                        <Grid size={{ xs: 2.5 }} className="wordWrap">
                            {item.name}
                        </Grid>

                        <Grid size={{ xs: 2.5 }} className="wordWrap">
                            {item.size && formatBytes(item.size)}
                        </Grid>

                        <Grid size={{ xs: 2.5 }} className="wordWrap">
                            {item.modifiedDate && getLocalDateWithTimeInString(new Date(item.modifiedDate))}
                        </Grid>

                        <Grid size={{ xs: 2 }} className="wordWrap">
                            <Button sx={{ "& .MuiButton-startIcon": { marginRight: "0px" } }} variant="outlined" startIcon={<DownloadIcon />}
                                onClick={portalAction(portalScreenContext('SupportingDocuments'), 'SupportingDocuments.onClick.downloadFile', () => item?.name && downloadFile(item?.name))} >
                                Download
                            </Button>
                        </Grid>

                        <Grid size={{ xs: 2 }} className="wordWrap">
                            <Button sx={{ "& .MuiButton-startIcon": { marginRight: "0px" } }} variant="outlined"
                                startIcon={<DeleteIcon />} onClick={portalAction(portalScreenContext('SupportingDocuments'), 'SupportingDocuments.onClick.setFileToDelete', () => {
                                    item?.name && setFileToDelete(item?.name);
                                    setDeleteDocumentConfirmation(true)
                                })} disabled={isSpaEditabe}>
                                Delete
                            </Button>
                        </Grid>

                        <Grid size={{ xs: 0.5 }} className="wordWrap"></Grid>
                    </React.Fragment>
                ))}

                {pcsEventDocuments?.length === 0 && <>
                    <Grid size={{ xs: 7.5 }} sx={{ textAlign: 'center' }}>No documents found.</Grid>
                    <Grid size={{ xs: 4.5 }}></Grid>
                </>}

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>

            <Grid container rowSpacing={4} sx={{ ml: '2%' }} display={{ xs: 'flex', sm: 'flex', md: 'none' }}>
                {pcsEventDocuments?.map((item) => (
                    <React.Fragment key={item.name}>
                        <Grid key={item.name} size={{ xs: 12 }}>
                            <EventDocumentsMediaCard pcsEventDocument={item} isSpaEditabe={isSpaEditabe}
                                downloadFile={downloadFile} setFileToDelete={(name: string) => setFileToDelete(name)}
                                setDeleteDocumentConfirmation={(isDelete: boolean) => setDeleteDocumentConfirmation(isDelete)} />
                        </Grid>
                    </React.Fragment>))}

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>

            <Grid container rowSpacing={4} sx={{ ml: '2%' }}>
                <Grid size={{ xs: 5 }} sx={{ textAlign: 'start' }}>
                    <Button variant="outlined" color="primary" onClick={portalAction(portalScreenContext('SupportingDocuments'), 'SupportingDocuments.onClick.handleBack', handleBack)}>
                        Back to previous step
                    </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={onNextClick}>
                        {MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>
        </Box>

        <PcsEventDialog showDialog={showDocumentError}
            title={'Please add required files.'}
            content={<SupportingDocumentsHeader />}
            skipButtonClick={portalAction(portalScreenContext('SupportingDocuments'), 'SupportingDocuments.skipButtonClick.setShowDocumentError', () => setShowDocumentError(false))}
            showYesNoButtons={false} />

        {deleteDocumentConfirmation &&
            <UnsavedChangesDialog showDialog={true}
                title={'Are you sure you want to delete ' + fileToDelete + '?'}
                subTitle={`You will loose the changes on clicking "YES".`}
                yesButtonClick={portalAction(portalScreenContext('SupportingDocuments'), 'SupportingDocuments.yesButtonClick.deleteDocument', () => {
                    fileToDelete !== '' && deleteDocument(fileToDelete);
                    setDeleteDocumentConfirmation(false);
                })}
                noButtonClick={portalAction(portalScreenContext('SupportingDocuments'), 'SupportingDocuments.noButtonClick.setDeleteDocumentConfirmation', () => setDeleteDocumentConfirmation(false))}
                showYesNoButtons={true} />}
    </FormProvider>
}
